import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { tripService } from '../api';
import { Trip } from '../types';
import Sidebar from '../components/Sidebar';
import './TripList.css';

const TripList: React.FC = () => {
  const { user } = useAuth();
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [inactivatingTripId, setInactivatingTripId] = useState<string | null>(null);

  useEffect(() => {
    loadTrips();
  }, [currentPage, pageSize]);

  const loadTrips = async () => {
    setLoading(true);
    try {
      const paginatedData = await tripService.getPaginatedTrips({
        pageNo: currentPage,
        pageSize: pageSize,
      });
      setTrips(paginatedData.data || []);
      setTotalPages(paginatedData.pages || 0);
      setTotalElements(paginatedData.total || 0);
    } catch (error) {
      console.error('Error loading trips:', error);
      setTrips([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  const handleInactivateToggle = async (tripId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setInactivatingTripId(tripId);
      await tripService.inactivateTrip(tripId);

      // Update the trip's active status in the local state
      setTrips(trips.map(trip =>
        trip.id === tripId
          ? { ...trip, active: !trip.active }
          : trip
      ));
    } catch (error) {
      console.error('Error toggling trip active status:', error);
      alert('Failed to update trip status. Please try again.');
    } finally {
      setInactivatingTripId(null);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCityNames = (cities?: any[]) => {
    if (!cities || cities.length === 0) return '-';
    return cities.map(city => city.name).join(', ');
  };

  return (
    <div className="layout">
      <Sidebar />
      <div className="main-content">
        <div className="top-bar">
          <div className="user-info">
            <span className="user-icon">👤</span>
            <span className="user-email">{user?.email || 'admin@roameez.com'}</span>
          </div>
        </div>

        <div className="page-container">
          <div className="page-header">
            <div>
              <h1 className="page-title">Trips ({totalElements})</h1>
              <div className="breadcrumb">
                <span>Dashboard</span>
                <span className="breadcrumb-separator">›</span>
                <span>Trips</span>
              </div>
            </div>
            <div className="page-actions">
              <select
                className="page-size-select"
                value={pageSize}
                onChange={handlePageSizeChange}
              >
                <option value="10">10 per page</option>
                <option value="20">20 per page</option>
                <option value="50">50 per page</option>
                <option value="100">100 per page</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="loading-container">Loading...</div>
          ) : trips && trips.length > 0 ? (
            <>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Image</th>
                      <th>Trip Name</th>
                      <th>Description</th>
                      <th>Cities</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trips.map((trip) => (
                      <tr
                        key={trip.id}
                        onClick={() => window.location.href = `/trips/${trip.id}`}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{trip.id}</td>
                        <td>
                          {trip.imageUrl ? (
                            <img
                              src={trip.imageUrl}
                              alt={trip.name}
                              className="trip-thumbnail"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                                if (placeholder) placeholder.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className="trip-thumbnail-placeholder"
                            style={{ display: trip.imageUrl ? 'none' : 'flex' }}
                          >
                            ✈️
                          </div>
                        </td>
                        <td className="trip-name-cell">{trip.name}</td>
                        <td className="trip-description-cell">
                          {trip.description || '-'}
                        </td>
                        <td>{getCityNames(trip.cities)}</td>
                        <td>{formatDate(trip.startDate)}</td>
                        <td>{formatDate(trip.endDate)}</td>
                        <td>
                          <span className={`status-badge ${trip.active === false ? 'inactive' : 'active'}`}>
                            {trip.active === false ? 'Inactive' : 'Active'}
                          </span>
                        </td>
                        <td>
                          <button
                            className={`toggle-btn ${trip.active === false ? 'inactive' : 'active'}`}
                            onClick={(e) => handleInactivateToggle(trip.id, e)}
                            disabled={inactivatingTripId === trip.id}
                          >
                            {inactivatingTripId === trip.id ? (
                              'Processing...'
                            ) : trip.active === false ? (
                              'Activate'
                            ) : (
                              'Inactivate'
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    ← Previous
                  </button>
                  <span className="pagination-info">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-data">
              <p>No trips found.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TripList;
